import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { API_BASE } from '../config';

const VerifyEmail: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email…');

  const verify = useCallback(async () => {
    if (!token) {
      setState('error');
      setMessage('This verification link is missing its token.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      setState('ok');
      setMessage('Your email is verified. You can continue to ManSearch.');
    } catch (err: unknown) {
      setState('error');
      setMessage(err instanceof Error ? err.message : 'Verification failed');
    }
  }, [token]);

  useEffect(() => {
    verify();
  }, [verify]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-navy-900 dark:to-navy-900 flex items-center justify-center p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gradient">ManSearch</h1>
        </div>

        <div className="bg-white dark:bg-navy-800 rounded-xl shadow-soft border border-gray-200 dark:border-slate-700 p-8 text-center">
          {state === 'loading' && <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-4" />}
          {state === 'ok' && <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" />}
          {state === 'error' && <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />}

          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {state === 'ok' ? 'Email verified' : state === 'error' ? 'Verification failed' : 'Verifying…'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-400 mt-2">{message}</p>

          {state !== 'loading' && (
            <Link
              to="/"
              className="inline-block mt-6 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all"
            >
              Continue to ManSearch
            </Link>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
